import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { FatalError } from "@/components/FatalError";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useInterests } from "@/hooks/useInterests";
import { useMajors } from "@/hooks/useMajors";
import { useLocations } from "@/hooks/useLocations";
import { useUpdateProfile } from "@/hooks/useUpdateProfile";
import {
  DropdownMultiSelect,
  SearchableCombobox,
  SearchableOption,
} from "@/components/FormInputs";
import { SelectOption } from "@/components/FormInputs/types";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";

interface EditProfileFormData {
  firstName: string;
  lastName: string;
  bio: string;
  hometown: SearchableOption | null;
  interests: string[];
  majors: string[];
  profilePhoto: File | null;
}

const BIO_MAX_LENGTH = 500;
const INTERESTS_MAX_COUNT = 20;
const MAJORS_MAX_COUNT = 2;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const EditProfile = () => {
  const { t } = useTranslation();
  const {
    profile,
    loading: profileLoading,
    error: profileError,
  } = useMyProfile();
  const { loading: interestsLoading, data: interests } = useInterests();
  const { loading: majorsLoading, data: majors } = useMajors();

  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const [updateProfile, { loading: updateLoading }] = useUpdateProfile({
    onError: (error) => {
      let errorMessage = "An unexpected error occurred";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      toast.error(t("manageProfile.editProfile.updateError"), {
        description: errorMessage,
      });
    },
  });

  const {
    locations: locationOptions,
    loading: locationsLoading,
    pageInfo: locationPageInfo,
    loadMore: loadMoreLocations,
  } = useLocations(locationSearchTerm);

  const loading = profileLoading || interestsLoading || majorsLoading;
  const error =
    profileError || (!loading && (interests === null || majors === null));

  const interestOptions: SelectOption[] = useMemo(
    () =>
      interests?.map((interest) => ({
        label: interest.name,
        value: interest.id,
      })) || [],
    [interests],
  );

  const majorOptions: SelectOption[] = useMemo(
    () =>
      majors?.map((major) => ({
        label: major.name,
        value: major.id,
      })) || [],
    [majors],
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { isDirty, errors },
  } = useForm<EditProfileFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      bio: "",
      hometown: null,
      interests: [],
      majors: [],
      profilePhoto: null,
    },
    values: profile
      ? {
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          bio: profile.bio || "",
          hometown: profile.locationCity
            ? {
                id: profile.locationCity.id,
                displayString: profile.locationCity.displayString,
              }
            : null,
          interests: profile.interests?.map((interest) => interest.id) || [],
          majors: profile.majors?.map((major) => major.id) || [],
          profilePhoto: null,
        }
      : undefined,
  });

  const onSubmit = async (data: EditProfileFormData) => {
    if (!data.firstName.trim()) {
      setError("firstName", {
        type: "manual",
        message: t("manageProfile.editProfile.firstNameBlank"),
      });
      return;
    }
    if (!data.lastName.trim()) {
      setError("lastName", {
        type: "manual",
        message: t("manageProfile.editProfile.lastNameBlank"),
      });
      return;
    }

    if (!profile?.id) return;

    const result = await updateProfile({
      userId: profile.id,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      bio: data.bio || null,
      locationCityId: data.hometown?.id || null,
      interestIds: data.interests.length > 0 ? data.interests : null,
      majorIds: data.majors.length > 0 ? data.majors : null,
      profilePhoto: data.profilePhoto || undefined,
    });

    if (result?.errors?.length) {
      toast.error(t("manageProfile.editProfile.updateError"), {
        description: result.errors[0].message,
      });
    } else {
      toast.success(t("manageProfile.editProfile.updateSuccess"));
      reset({
        ...data,
        profilePhoto: null,
      });
      setImagePreview(null);
    }
  };

  const handleInterestsUpdate = useCallback(
    (field: { onChange: (value: string[]) => void; value: string[] }) => {
      return (newValue: string[]) => {
        if (
          newValue.length > field.value.length &&
          newValue.length > INTERESTS_MAX_COUNT
        ) {
          setError("interests", {
            type: "manual",
            message: t("manageProfile.editProfile.interestsLimit"),
          });
          return;
        }
        clearErrors("interests");
        field.onChange(newValue);
      };
    },
    [t, setError, clearErrors],
  );

  const handleMajorsUpdate = useCallback(
    (field: { onChange: (value: string[]) => void; value: string[] }) => {
      return (newValue: string[]) => {
        if (
          newValue.length > field.value.length &&
          newValue.length > MAJORS_MAX_COUNT
        ) {
          setError("majors", {
            type: "manual",
            message: t("manageProfile.editProfile.majorsLimit"),
          });
          return;
        }
        clearErrors("majors");
        field.onChange(newValue);
      };
    },
    [t, setError, clearErrors],
  );

  const validateAndSetImage = useCallback(
    (file: File) => {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        setError("profilePhoto", {
          type: "manual",
          message: t("manageProfile.editProfile.imageFormatError"),
        });
        return false;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        setError("profilePhoto", {
          type: "manual",
          message: t("manageProfile.editProfile.imageSizeError"),
        });
        return false;
      }

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      clearErrors("profilePhoto");
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setValue("profilePhoto", file, { shouldDirty: true });
      return true;
    },
    [setValue, t, imagePreview, setError, clearErrors],
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        validateAndSetImage(file);
      }
    },
    [validateAndSetImage],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);

      const file = event.dataTransfer.files?.[0];
      if (file) {
        validateAndSetImage(file);
      }
    },
    [validateAndSetImage],
  );

  const handleUpdateClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCancel = useCallback(() => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    reset();
    setImagePreview(null);
    clearErrors();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [reset, imagePreview, clearErrors]);

  if (error) {
    return <FatalError />;
  }

  if (loading) {
    return (
      <Card className="w-[760px]">
        <CardHeader>
          <CardTitle className="text-xl">
            {t("manageProfile.editProfile.title")}
          </CardTitle>
          <CardDescription>
            {t("manageProfile.editProfile.subTitle")}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6 p-10 px-8">
          {/* Profile Photo Skeleton */}
          <Skeleton className="h-[104px] w-full rounded-2xl" />

          {/* First Name Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>

          {/* Last Name Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>

          {/* Bio Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-4 w-12" />
          </div>

          {/* Hometown Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>

          {/* Interests Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>

          {/* Major Skeleton */}
          <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>

        <CardFooter className="justify-end gap-3 border-t px-6 py-4">
          <Skeleton className="h-10 w-16" />
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-[760px]">
      <CardHeader>
        <CardTitle className="text-xl">
          {t("manageProfile.editProfile.title")}
        </CardTitle>
        <CardDescription>
          {t("manageProfile.editProfile.subTitle")}
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="flex flex-col gap-6 p-10 px-8">
          {/* Profile Photo */}
          <div className="flex flex-col gap-2">
            <div
              className={`flex items-center gap-4 rounded-2xl p-6 outline outline-1 outline-offset-[-1px] ${isDragging ? "bg-primary/5 outline-primary" : errors.profilePhoto ? "outline-destructive" : "outline-border"}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {imagePreview || profile?.profilePhotoUrl ? (
                <img
                  className="h-16 w-16 rounded-lg object-cover"
                  src={imagePreview || profile?.profilePhotoUrl || ""}
                  alt={t("manageProfile.editProfile.profile")}
                />
              ) : (
                <div className="h-16 w-16 rounded-lg bg-muted" />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={handleUpdateClick}
                className="flex h-10 items-center gap-2 rounded-md bg-background px-4 py-2 text-sm font-medium text-foreground outline outline-1 outline-offset-[-1px] outline-input hover:bg-muted"
              >
                <Upload className="h-4 w-4" />
                {t("global.update")}
              </button>
            </div>
            {errors.profilePhoto && (
              <p className="text-xs text-destructive">
                {errors.profilePhoto.message}
              </p>
            )}
          </div>

          {/* First Name */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.firstName")}
            </label>
            <input
              type="text"
              placeholder={t("manageProfile.editProfile.firstNameHint")}
              {...register("firstName")}
              className={`h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] ${
                errors.firstName ? "outline-destructive" : "outline-input"
              }`}
            />
            {errors.firstName && (
              <p className="text-xs text-destructive">
                {errors.firstName.message}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.lastName")}
            </label>
            <input
              type="text"
              placeholder={t("manageProfile.editProfile.lastNameHint")}
              {...register("lastName")}
              className={`h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] ${
                errors.lastName ? "outline-destructive" : "outline-input"
              }`}
            />
            {errors.lastName && (
              <p className="text-xs text-destructive">
                {errors.lastName.message}
              </p>
            )}
          </div>

          {/* Bio */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.bio")}
            </label>
            <textarea
              placeholder={t("manageProfile.editProfile.bioHint")}
              {...register("bio")}
              className="h-20 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] outline-input"
              maxLength={BIO_MAX_LENGTH}
            />
            <span
              className={`text-sm ${watch("bio").length >= BIO_MAX_LENGTH ? "text-destructive" : "text-muted-foreground"}`}
            >
              {watch("bio").length}/{BIO_MAX_LENGTH}
            </span>
          </div>

          {/* Hometown */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.hometown")}
            </label>
            <Controller
              name="hometown"
              control={control}
              render={({ field }) => (
                <SearchableCombobox
                  placeholder={t("manageProfile.editProfile.hometownHint")}
                  searchPlaceholder={t(
                    "manageProfile.editProfile.hometownHint",
                  )}
                  emptyResults="No locations found"
                  value={field.value}
                  onSelect={field.onChange}
                  onSearchChange={setLocationSearchTerm}
                  options={locationOptions}
                  loading={locationsLoading}
                  hasMore={locationPageInfo.hasNextPage}
                  onLoadMore={loadMoreLocations}
                />
              )}
            />
          </div>

          {/* Interests */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.interests")}
            </label>
            <Controller
              name="interests"
              control={control}
              render={({ field }) => (
                <DropdownMultiSelect
                  placeholder={t("manageProfile.editProfile.interestsHint")}
                  options={interestOptions}
                  value={field.value}
                  onUpdate={handleInterestsUpdate(field)}
                  allowSearch={true}
                  emptyResults={t("manageProfile.editProfile.interestsEmpty")}
                />
              )}
            />
            {errors.interests && (
              <p className="text-xs text-destructive">
                {errors.interests.message}
              </p>
            )}
          </div>

          {/* Major */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("manageProfile.editProfile.major")}
            </label>
            <Controller
              name="majors"
              control={control}
              render={({ field }) => (
                <DropdownMultiSelect
                  placeholder={t("manageProfile.editProfile.majorsHint")}
                  options={majorOptions}
                  value={field.value}
                  onUpdate={handleMajorsUpdate(field)}
                  allowSearch={true}
                  emptyResults={t("manageProfile.editProfile.majorsEmpty")}
                />
              )}
            />
            {errors.majors && (
              <p className="text-xs text-destructive">
                {errors.majors.message}
              </p>
            )}
          </div>
        </CardContent>

        <CardFooter className="justify-between border-t px-6 py-4">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
          >
            {t("global.cancel")}
          </button>
          <button
            type="submit"
            disabled={
              !isDirty || updateLoading || Object.keys(errors).length > 0
            }
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("global.save")}
          </button>
        </CardFooter>
      </form>
    </Card>
  );
};
